import { Theme } from "@emotion/react";
import { Box, SxProps } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import copy from "clipboard-copy";
import React, { useCallback, useMemo, useState } from "react";
import { flushSync } from "react-dom";

/**
 * Options to control if the tooltip renders.
 */
interface TooltipOptions {
  disableHoverMessage?: boolean;
  disableCopiedMessage?: boolean;
  /**
   * Shown on hover instead of "Click to copy". Use when the value itself is
   * truncated on screen and hovering is the only way to read the rest of it --
   * the copy affordance should not cost the reader the value.
   */
  hoverMessage?: string;
}

export interface CopyableTypographyProps {
  text?: string | null;
  /** When set, this is shown instead of text but the full text is still copied. */
  displayText?: string | null;
  subtext?: string | undefined | null;
  variant?: "body1" | "body2" | "caption";
  stopPropagation?: boolean;
  sx?: SxProps<Theme>;
  tooltipOptions?: TooltipOptions;
  /** The rendered value element, so a caller can measure whether it is clipped. */
  ref?: React.Ref<HTMLDivElement>;
  /** What the value is, e.g. "EPA ID", so a screen reader hears "Copy EPA ID VA…". */
  copyLabel?: string;
  /**
   * Inside a DataGrid cell, pass the cell's `tabIndex` so the value joins the
   * grid's roving focus instead of adding a Tab stop to every row.
   */
  tabIndex?: 0 | -1;
  /**
   * Off when an enclosing control is already the focus target and owns Enter
   * and Space, so one button is not nested in another. A click still copies.
   */
  focusable?: boolean;
}

const DEFAULT_HOVER_MESSAGE = "Click to copy";
const DEFAULT_COPIED_MESSAGE = "Copied to clipboard";

const focusRingSx: SxProps<Theme> = {
  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: "1px" },
};

/**
 * A live region only speaks when its text changes, so repeat copies alternate a
 * trailing no-break space to be announced each time.
 */
function copiedAnnouncement(copyCount: number) {
  if (copyCount === 0) {
    return "";
  }
  return copyCount % 2 === 1 ? DEFAULT_COPIED_MESSAGE : `${DEFAULT_COPIED_MESSAGE}\u00A0`;
}

function CopyableTypography({
  text,
  displayText,
  subtext,
  variant = "body1",
  stopPropagation = true,
  sx,
  tooltipOptions = {},
  ref,
  copyLabel,
  tabIndex = 0,
  focusable = true,
}: CopyableTypographyProps) {
  const visibleText = displayText ?? text;
  const [copied, setCopied] = useState(false);
  const [copyCount, setCopyCount] = useState(0);
  // Mounted on first copy, so a grid of copyable values isn't a grid of status regions.
  const [statusMounted, setStatusMounted] = useState(false);

  const handleClose = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    setCopied(false);
  }, []);

  const copyText = useCallback(async () => {
    // Copying nothing would wipe whatever the reader already had on the
    // clipboard, which is a worse outcome than the click doing nothing.
    if (!text) {
      return;
    }
    await copy(text);
    // A live region announces only a change to text it already holds, so it is
    // committed empty first. Screen readers activate with a bare click, with no
    // focus or pointer event to mount it any earlier.
    flushSync(() => setStatusMounted(true));
    setCopied(true);
    setCopyCount((count) => count + 1);
  }, [text]);

  const handleClick = useCallback(
    async (event: React.MouseEvent) => {
      if (stopPropagation) {
        event.stopPropagation();
        event.preventDefault();
      }
      await copyText();
    },
    [stopPropagation, copyText]
  );

  // Always contained: a grid treats Space as "page down", and a key that
  // activates this button should not also drive whatever holds it.
  const handleKeyDown = useCallback(
    async (event: React.KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      await copyText();
    },
    [copyText]
  );

  const tooltipTitle = useMemo(() => {
    if (copied && !tooltipOptions.disableCopiedMessage) {
      return DEFAULT_COPIED_MESSAGE;
    }
    if (!tooltipOptions.disableHoverMessage) {
      return tooltipOptions.hoverMessage ?? DEFAULT_HOVER_MESSAGE;
    }
    return undefined;
  }, [tooltipOptions, copied]);

  // A value with nothing to copy stays plain text rather than a button that does nothing.
  const isButton = focusable && Boolean(text);
  const buttonProps = isButton
    ? {
        role: "button",
        tabIndex,
        "aria-label": copyLabel ? `Copy ${copyLabel} ${text}` : `Copy ${text}`,
        onKeyDown: handleKeyDown,
      }
    : {};

  return (
    <>
      <Tooltip placement="bottom-start" title={tooltipTitle} onClose={handleClose}>
        <Typography
          ref={ref}
          variant={variant}
          component="div"
          onClick={handleClick}
          style={{ cursor: "pointer" }}
          sx={[isButton && focusRingSx, ...(Array.isArray(sx) ? sx : [sx])]}
          {...buttonProps}
        >
          {visibleText}
          {subtext && (
            <Typography variant="caption" fontStyle="italic" color="text.secondary">
              {subtext}
            </Typography>
          )}
        </Typography>
      </Tooltip>
      {/* Outside the button, whose children a screen reader may treat as presentational. */}
      {statusMounted && (
        <Box component="span" role="status" sx={visuallyHidden}>
          {copiedAnnouncement(copyCount)}
        </Box>
      )}
    </>
  );
}

export default CopyableTypography;
